import { Component, OnInit } from '@angular/core';
import { ICatalog } from '../../../models/catalogs.models';
import { debounceTime, Observable, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { IPriceBookSimple, PriceBookTypeValue } from 'src/app/features/pricebook/models/pricebook.model';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { PricebookService } from 'src/app/features/pricebook/services/pricebook.service';
import { UiModule } from 'src/app/ui/ui.module';
import { FormControl } from '@angular/forms';
import { TogglePricebookForCatalog } from 'src/app/features/pricebook/store/pricebook.actions';
import { MatDialog } from '@angular/material/dialog';
import { NewPricebookComponent } from 'src/app/features/pricebook/components/new-pricebook/new-pricebook.component';
import { AddPromotionToCatalog } from 'src/app/features/catalogs/store/catalogs.actions';
import { IProduct } from 'src/app/features/produits/models/products.models';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { TranslateService } from '@ngx-translate/core';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';

@Component({
  selector: 'app-catalog-price-rules',
  standalone: true,
  imports: [CommonModule, SharedModule, UiModule],
  templateUrl: './catalog-price-rules.component.html',
  styleUrls: ['./catalog-price-rules.component.scss']
})
export class CatalogPriceRulesComponent implements OnInit {
  catalog$: Observable<ICatalog | null> = this.store.select((state) => state.catalogs.catalog);
  catalog!: ICatalog | null;
  priceRules: IPriceBookSimple[] = [];
  pricingRules: IPriceBookSimple[] = [];
  promotions: IPriceBookSimple[] = [];
  filteredPromotions: IPriceBookSimple[] = [];

  searchInput = new FormControl('');

  destroy$ = new Subject<void>();

  public loyaltyEnabled = false;

  constructor(
    private store: Store<AppState>,
    private pricebookService: PricebookService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
  ) { }

  ngOnInit(): void {
    this.featureFlagService.loyaltyEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => this.loyaltyEnabled = enabled);

    this.catalog$.pipe(takeUntil(this.destroy$)).subscribe((catalog) => {
      this.catalog = catalog;
      this.priceRules = catalog?.deals || [];
      this.pricingRules = this.getPricingRules();
      this.promotions = this.getPromotions();
      this.filteredPromotions = this.promotions;
    });

    this.searchInput.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((value) => {
      this.filteredPromotions = this.promotions.filter((p) => p.name.includes(value || ''))
    })
  }

  getPricingRules(): IPriceBookSimple[] {
    return this.priceRules.filter(
      rule => this.pricebookService.getPricebookCategoryFromCalculationType(rule.calculation_type!) === 'pricing_rule'
    );
  }

  getPromotions(): IPriceBookSimple[] {
    return this.priceRules.filter(
      rule => this.pricebookService.getPricebookCategoryFromCalculationType(rule.calculation_type!) === 'promotion'
    );
  }

  getPricebookCalculationTypeDisplayText(type: PriceBookTypeValue): string {
    return this.pricebookService.getPricebookCalculationTypeDisplayText(type);
  }

  togglePromotionConfirmation(event: Event, promotion: IPriceBookSimple) {
    event.preventDefault();

    let title = '';
    let message = '';
    if (!promotion.active) {
      title = this.translateService.instant('Catalogs.new_catalog.price_book.enable_promotion');
      message = this.translateService.instant('Catalogs.new_catalog.price_book.enable_promotion_desc');
    } else {
      title = this.translateService.instant('Catalogs.new_catalog.price_book.disable_promotion');
      message = this.translateService.instant('Catalogs.new_catalog.price_book.disable_promotion_desc');
    }

    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        body_title: title,
        text: message,
        confirm_class: 'primary-btn',
      }
    })

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.togglePromotion(promotion);
      }
    });
  }

  togglePromotion(promotion: IPriceBookSimple): void {
    this.store.dispatch(TogglePricebookForCatalog({ catalogId: this.catalog?.id!, pricebookId: promotion.id }));
  }

  // TODO: This should stay disabled for now, we will add it back later when the promotion can get associated with a catalog directly
  addPromotion(): void {
    return;
    const dialogRef = this.dialog.open(NewPricebookComponent, {
      data: { category: 'promotion' },
      disableClose: true,
      position: { right: '0' },
      height: '100vh',
      width: '700px',
      panelClass: 'custom-side-dialog'
    });

    dialogRef.afterClosed().subscribe((pricebook: IPriceBookSimple) => {
      if (pricebook) {
        this.promotions.push(pricebook);
        this.filteredPromotions = this.promotions;
        this.store.dispatch(AddPromotionToCatalog({ promotion: pricebook }));
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
